import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/about", label: "About Us", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
  { to: "/login", label: "Client Login", id: "client-login" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-obsidian-900/85 backdrop-blur-xl border-b border-gold/20"
            : "bg-gradient-to-b from-obsidian-900/70 to-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="nav-link-home-logo"
            className="transition-opacity duration-300 hover:opacity-80"
          >
            <Logo size={48} withWordmark />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `font-sans text-[15px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ivory/80 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/reservation"
              data-testid="nav-cta-book-now"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gold px-7 py-3 font-sans text-[13px] font-semibold uppercase tracking-[0.12em] text-obsidian-900 shadow-[0_0_18px_rgba(212,175,55,0.3)] transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_34px_rgba(212,175,55,0.45)]"
            >
              Book Now
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-obsidian-900/97 backdrop-blur-2xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-1 px-8">
              {LINKS.map((l, i) => (
                <motion.span
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="block overflow-hidden"
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-4xl font-semibold ${
                        isActive ? "italic text-gold" : "text-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.span>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-8"
              >
                <Link
                  to="/reservation"
                  data-testid="nav-mobile-cta-book-now"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900"
                >
                  Book Now
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
